import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Play, ArrowCounterClockwise, Stop, CaretDoubleRight } from "@phosphor-icons/react";
import { WELCOME_VIDEO, TRAIN_VIDEO } from "@/data/feelingsEpisodes";
import { KidsVideoPlayer } from "./KidsVideoPlayer";

// Feelings Station welcome: Welcome.mp4 (own audio) → when it ends, All Aboard (own audio). No generated narration.
// Each clip is independently replayable; Stop or leaving the page cancels the queued second clip.
const CLIPS = [
  { ...WELCOME_VIDEO, id: "welcome", step: "1" },
  { ...TRAIN_VIDEO, id: "train", step: "2" },
];

export const WelcomeSequence = forwardRef(function WelcomeSequence(_props, ref) {
  const playerRef = useRef(null);
  const [current, setCurrent] = useState(0);
  const [autoStart, setAutoStart] = useState(false);
  const [queued, setQueued] = useState(false); // play All Aboard after Welcome ends
  const [playing, setPlaying] = useState(false);
  const [watched, setWatched] = useState([]);
  const queuedRef = useRef(false);
  const setQueue = (on) => { queuedRef.current = on; setQueued(on); };

  useEffect(() => () => setQueue(false), []); // navigating away cancels the chain (player unmount stops playback)

  // Whenever the Welcome clip is playing, All Aboard is queued (Stop clears it).
  const handlePlaying = (on) => { setPlaying(on); if (on && current === 0) setQueue(true); };

  const startClip = (index) => {
    if (index !== 0) setQueue(false);
    if (index === current) { playerRef.current?.replay(); return; }
    setCurrent(index);
    setAutoStart(true);
  };

  const stopAll = () => { setQueue(false); playerRef.current?.stop(); };
  useImperativeHandle(ref, () => ({ playWelcome: () => startClip(0), stop: stopAll }));

  const handleEnded = () => {
    setWatched((w) => (w.includes(current) ? w : [...w, current]));
    if (current === 0 && queuedRef.current) {
      setQueue(false);
      setCurrent(1);
      setAutoStart(true);
    }
  };

  const clip = CLIPS[current];
  return (
    <div className="flex flex-col" data-testid="welcome-sequence" data-current={clip.id} data-queued={queued || undefined}>
      <KidsVideoPlayer
        key={clip.id}
        ref={playerRef}
        src={clip.src}
        poster={clip.poster}
        captions={clip.captions}
        title={clip.title}
        playLabel={clip.title}
        autoPlay={autoStart}
        onEnded={handleEnded}
        onPlayingChange={handlePlaying}
        testid={`welcome-video-${clip.id}`}
        className="rounded-t-3xl"
      />
      <div className="px-4 sm:px-5 py-3 flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Welcome clips">
          {CLIPS.map((c, i) => {
            const isCurrent = i === current;
            const seen = watched.includes(i) || (isCurrent && playing); // "Replay" only once it has actually played
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => startClip(i)}
                aria-pressed={isCurrent}
                aria-label={`${seen ? "Replay" : "Play"} ${c.title}`}
                className={`inline-flex items-center gap-2 h-10 pl-2 pr-4 rounded-full font-nunito font-bold text-sm transition-colors focus-visible:ring-4 focus-visible:ring-[#b79cf0] ${isCurrent ? "bg-[#6a3fc7] text-white" : "bg-[#efe9fb] text-[#4a2e8a] hover:bg-[#e3d9f8]"}`}
                data-testid={`welcome-clip-${c.id}-btn`}
              >
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-extrabold ${isCurrent ? "bg-white/25" : "bg-white"}`}>{c.step}</span>
                {seen ? <ArrowCounterClockwise size={16} weight="bold" /> : <Play size={16} weight="fill" />}
                {c.title}
              </button>
            );
          })}
          {(playing || queued) && (
            <button type="button" onClick={stopAll} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white border border-[#d9ccf5] font-nunito font-bold text-sm text-[#4a2e8a] hover:bg-[#f3eefc] focus-visible:ring-4 focus-visible:ring-[#b79cf0]" data-testid="welcome-stop-btn">
              <Stop size={16} weight="fill" /> Stop
            </button>
          )}
        </div>
        <p className="font-nunito text-sm text-[#5b5572]" data-testid="welcome-sequence-status">
          {queued && current === 0 ? (
            <span className="inline-flex items-center gap-1.5"><CaretDoubleRight size={14} weight="bold" className="text-[#6a3fc7]" /> Up next: {CLIPS[1].title}</span>
          ) : (
            "Captions are on. Tap the video to pause."
          )}
        </p>
      </div>
    </div>
  );
});
